import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  CheckCircle, XCircle, Info, Loader2,
  RefreshCw, Check, BellOff,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import DesignerLayout from './DesignerLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  approved: {
    icon: CheckCircle,
    color: 'text-emerald-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    label: 'Approved',
  },
  rejected: {
    icon: XCircle,
    color: 'text-red-500',
    bg: 'bg-red-50',
    border: 'border-red-200',
    label: 'Rejected',
  },
  info: {
    icon: Info,
    color: 'text-blue-500',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    label: 'Info',
  },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const DesignerNotifications = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem('user_token');
    if (!token || !API_URL) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/designer/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(Array.isArray(data) ? data : []);
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to load notifications', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const markAllRead = async () => {
    const token = localStorage.getItem('user_token');
    if (!token || !API_URL) return;
    setMarkingAll(true);
    try {
      await fetch(`${API_URL}/api/designer/notifications/read-all`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast({ title: 'All notifications marked as read' });
    } catch {
      toast({ title: 'Error', description: 'Failed to mark notifications as read', variant: 'destructive' });
    } finally {
      setMarkingAll(false);
    }
  };

  const markOneRead = async (id) => {
    const token = localStorage.getItem('user_token');
    if (!token || !API_URL) return;
    try {
      await fetch(`${API_URL}/api/designer/notifications/${id}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch {}
  };

  const filtered = notifications.filter(n => {
    if (activeFilter === 'unread') return !n.is_read;
    if (activeFilter === 'approved') return n.type === 'approved';
    if (activeFilter === 'rejected') return n.type === 'rejected';
    return true;
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const FILTER_TABS = [
    { key: 'all',      label: 'All',       count: notifications.length },
    { key: 'unread',   label: 'Unread',    count: unreadCount },
    { key: 'approved', label: 'Approved',  count: notifications.filter(n => n.type === 'approved').length },
    { key: 'rejected', label: 'Rejected',  count: notifications.filter(n => n.type === 'rejected').length },
  ];

  return (
    <DesignerLayout>
      <div className="space-y-6 w-full">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
            <p className="text-gray-500 text-sm mt-0.5">
              Stay updated on your template submissions.
              {unreadCount > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-violet-600 font-semibold">
                  <span className="w-2 h-2 rounded-full bg-violet-500 inline-block" />
                  {unreadCount} unread
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchNotifications} className="gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={markAllRead}
                disabled={markingAll}
                className="gap-1.5"
              >
                {markingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Mark all read
              </Button>
            )}
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex bg-white border border-gray-200 rounded-xl p-1 gap-1 w-fit">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveFilter(tab.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeFilter === tab.key
                  ? 'bg-violet-600 text-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-1.5 text-xs ${activeFilter === tab.key ? 'text-violet-200' : 'text-gray-400'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Notifications list */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-7 h-7 animate-spin text-violet-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center">
              <BellOff className="w-6 h-6 text-gray-400" />
            </div>
            <p className="font-semibold text-gray-900">
              {activeFilter === 'unread' ? 'All caught up!' : 'No notifications'}
            </p>
            <p className="text-sm text-gray-400">
              {activeFilter === 'unread'
                ? 'No unread notifications.'
                : 'You\'ll be notified when your submissions are reviewed.'}
            </p>
            {activeFilter !== 'all' && (
              <Button variant="outline" size="sm" onClick={() => setActiveFilter('all')} className="mt-1">
                View all
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(n => {
              const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.info;
              const Icon = cfg.icon;
              return (
                <div
                  key={n.id}
                  className={`bg-white rounded-2xl border transition-all cursor-pointer
                    ${!n.is_read ? 'border-violet-200 shadow-sm' : 'border-gray-100'}
                    hover:shadow-sm`}
                  onClick={() => {
                    if (!n.is_read) markOneRead(n.id);
                    if (n.template_id) navigate('/designer/submissions');
                  }}
                >
                  <div className="px-5 py-4 flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-xl ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Icon className={`w-5 h-5 ${cfg.color}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          {n.template_title && (
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
                              {n.template_title}
                            </p>
                          )}
                          <p className={`text-sm ${!n.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                            {n.message}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {!n.is_read && (
                            <span className="w-2.5 h-2.5 rounded-full bg-violet-500 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                          <Icon className="w-3 h-3" /> {cfg.label}
                        </span>
                        <span className="text-xs text-gray-400">{formatDate(n.created_at)}</span>
                        {!n.is_read && (
                          <button
                            className="text-xs text-violet-600 hover:text-violet-700 font-medium ml-auto"
                            onClick={e => { e.stopPropagation(); markOneRead(n.id); }}
                          >
                            Mark as read
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action strip for approval/rejection */}
                  {(n.type === 'approved' || n.type === 'rejected') && (
                    <div className={`px-5 py-2.5 border-t ${n.type === 'approved' ? 'border-emerald-100 bg-emerald-50/50' : 'border-red-100 bg-red-50/50'} rounded-b-2xl`}>
                      <button
                        className={`text-xs font-semibold ${n.type === 'approved' ? 'text-emerald-700' : 'text-red-700'} hover:underline`}
                        onClick={e => { e.stopPropagation(); navigate('/designer/submissions'); }}
                      >
                        {n.type === 'approved' ? 'View approved template →' : 'View feedback & resubmit →'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer tip */}
        {!loading && notifications.length > 0 && (
          <p className="text-xs text-gray-400 text-center pb-2">
            Showing {filtered.length} of {notifications.length} notifications
          </p>
        )}
      </div>
    </DesignerLayout>
  );
};

export default DesignerNotifications;
